import { IComboBox, IComboBoxOption, Icon, IRenderFunction, ISelectableOption, Stack } from '@fluentui/react';
import {
  ComboBox,
  comboboxLoader,
  comboboxOptionText,
  comboboxWithFooterStyles,
  IconName,
  Loader,
  LoaderType,
} from '@h2oai/ui-kit';
import debounce from 'lodash.debounce';
import { FormEvent, useCallback, useMemo, useRef, useState } from 'react';

import type { User } from '../../../../authz/gen/ai/h2o/user/v1/user_pb';
import { useUsers } from '../../../../authz/providers/UsersProvider';
import { dropdownStyles } from './styles';
import { MembersDropdownProps } from './types';

const onRenderOption: IRenderFunction<ISelectableOption> = (item) => {
  if (!item) return null;

  return item?.data?.icon ? (
    <span style={{ display: 'flex', maxWidth: '100%' }}>
      <Icon iconName={item.data.icon} style={{ marginRight: 10 }} title={item.data?.iconTitle || item.data.icon} />
      <span style={comboboxOptionText} title={item.text}>
        {item.text}
      </span>
    </span>
  ) : (
    <span style={comboboxOptionText} title={item.text}>
      {item.text}
    </span>
  );
};

export const MembersDropdown = (props: MembersDropdownProps) => {
  const { onUserChange, prepopulatedUser, disableOptions, fetchGroups = false, disabled = false } = props;
  const { usersAndGroups, users, listUsers, listGroupsAndUsers, loading } = useUsers();

  const comboboxRef = useRef<IComboBox>(null);
  const [searchedMembers, setSearchedMembers] = useState<User[] | undefined>();
  const membersOptions = useMemo((): IComboBoxOption[] => {
    const members = fetchGroups ? usersAndGroups : users;
    const result =
      (searchedMembers ?? members)?.map((member) => ({
        key: member.name || '',
        text: member.displayName || (member as User)?.loginPrincipal || 'Unknown',
        disabled: disableOptions?.includes(String(member?.name)) ?? false,
        data: member.name?.includes('groups/') ? { ...member, icon: IconName.Group } : member,
      })) || [];

    return result.length > 0 ? result : [{ text: 'No items found', key: 'no-data-item', itemType: 2 }];
  }, [fetchGroups, usersAndGroups, users, searchedMembers, disableOptions]);

  const handleSearch = useCallback(
    async (value: string) => {
      let members;

      if (fetchGroups) {
        const response = await listGroupsAndUsers(value);
        members = [...(response?.groups || []), ...(response?.users || [])];
      } else {
        members = (await listUsers(value)) || [];
      }

      setSearchedMembers(value ? members : undefined);
    },
    [fetchGroups, listGroupsAndUsers, listUsers]
  );

  const handleSearchDebounce = useMemo(
    () =>
      debounce((value: string) => {
        void handleSearch(value);
      }, 500),
    [handleSearch]
  );

  const handleMemberChange = (_: FormEvent<IComboBox>, option?: IComboBoxOption | undefined) => {
    onUserChange(option?.data);
    void handleSearch('');
  };

  return (
    <Stack>
      <ComboBox
        text={prepopulatedUser?.displayName || ''}
        label={props.label}
        useComboBoxAsMenuWidth
        placeholder="Select a user"
        autoComplete="on"
        options={membersOptions}
        onChange={handleMemberChange}
        onInputValueChange={handleSearchDebounce}
        onRenderOption={onRenderOption}
        selectedKey={prepopulatedUser?.name}
        componentRef={comboboxRef}
        onClick={() => comboboxRef.current?.focus(true)}
        styles={[comboboxWithFooterStyles, dropdownStyles]}
        disabled={disabled}
      />
      <Loader type={LoaderType.progressIndicator} styles={comboboxLoader} hidden={!loading} />
    </Stack>
  );
};
